import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_PREFIX = 'fig_market_cart';

// Composite key: same product in different variants = separate lines
function cartKey(item) {
  return item.variantId ? `${item.apiid}_${item.variantId}` : item.apiid;
}

const initialState = {
  items: [],
  isDrawerOpen: false,
};

function cartReducer(state, action) {
  switch (action.type) {
    case 'LOAD_CART':
      return { ...state, items: action.payload };

    case 'ADD_ITEM': {
      const key = cartKey(action.payload);
      const existing = state.items.find(item => cartKey(item) === key);
      let newItems;
      if (existing) {
        // For custom donations, update the amount instead of stacking quantity
        if (action.payload.isDonation && action.payload.variantId === 'custom') {
          newItems = state.items.map(item =>
            cartKey(item) === key
              ? { ...item, PriceAdjustment: action.payload.PriceAdjustment }
              : item
          );
        } else {
          newItems = state.items.map(item =>
            cartKey(item) === key
              ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) }
              : item
          );
        }
      } else {
        newItems = [...state.items, { ...action.payload, cartKey: key, quantity: action.payload.quantity || 1 }];
      }
      return { ...state, items: newItems, isDrawerOpen: true };
    }

    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(item => cartKey(item) !== action.payload) };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          cartKey(item) === action.payload.cartKey
            ? { ...item, quantity: Math.max(1, action.payload.quantity) }
            : item
        ),
      };

    case 'CLEAR_CART':
      return { ...state, items: [] };

    case 'TOGGLE_DRAWER':
      return { ...state, isDrawerOpen: !state.isDrawerOpen };

    case 'SET_DRAWER':
      return { ...state, isDrawerOpen: action.payload };

    default:
      return state;
  }
}

export const CartProvider = ({ children, storeId }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const storageKey = storeId ? `${CART_STORAGE_PREFIX}_${storeId}` : CART_STORAGE_PREFIX;
  const loaded = useRef(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        dispatch({ type: 'LOAD_CART', payload: JSON.parse(saved) });
      }
      loaded.current = true;
    } catch (e) {
      console.error('Failed to load cart:', e);
      loaded.current = true;
    }
  }, [storageKey]);

  // Persist to localStorage on changes
  useEffect(() => {
    if (loaded.current) {
      localStorage.setItem(storageKey, JSON.stringify(state.items));
    }
  }, [state.items, storageKey]);

  const cartCount = state.items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = state.items.reduce((sum, item) => {
    const effectivePrice = Number(item.Price) + Number(item.PriceAdjustment || 0);
    return sum + effectivePrice * item.quantity;
  }, 0);

  const value = {
    items: state.items,
    isDrawerOpen: state.isDrawerOpen,
    cartCount,
    subtotal,
    addItem: (product, quantity = 1) => dispatch({ type: 'ADD_ITEM', payload: { ...product, quantity } }),
    removeItem: (key) => dispatch({ type: 'REMOVE_ITEM', payload: key }),
    updateQuantity: (key, quantity) => dispatch({ type: 'UPDATE_QUANTITY', payload: { cartKey: key, quantity } }),
    clearCart: () => dispatch({ type: 'CLEAR_CART' }),
    toggleDrawer: () => dispatch({ type: 'TOGGLE_DRAWER' }),
    setDrawerOpen: (open) => dispatch({ type: 'SET_DRAWER', payload: open }),
    cartKey,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
